import React, { useState, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Loader2, ChevronDown } from 'lucide-react';
import { useMobileDetection } from '../../hooks/useMobileDetection';
import { MobileRestrictionMessage } from '@/components/ui/MobileRestrictionMessage';
import { format } from 'date-fns';
import DateRangePicker from '@/components/ui/DateRangePicker';
import { userService } from '../../_services';
import Calendar from '../../components/css/dark/img/date_icon.png';
import * as signalR from '@aspnet/signalr';

function CreateNotification({ text, closeCreateNotification, OpenNotification, cPage }) {
    const [loading, setLoading] = useState(false);
    const [selectValue, setSelectValue] = useState([]);
    const [notContent, setNotContent] = useState('Notification Content here');
    const [dateTime, setDateTime] = useState('Select Date & Time');
    const [pickerDate, setPickerDate] = useState('');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [notifyError, setNotifyError] = useState('');
    const [dateTimeError, setDateTimeError] = useState('');
    const [mhError, setMhError] = useState('');
    const [expanded, setExpanded] = useState(false);
    const isMobile = useMobileDetection();

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const options = [
        { value: 'Broker' },
        { value: 'Admin' },
        { value: 'Sub Admin' },
        { value: 'Wholesaler' },
        { value: 'Distributor' },
        { value: 'All' },
        { value: 'Email Campaign' },
        { value: 'Mail Campaign' },
        { value: 'Web Campaign' }
    ];

    const handleCalendar = useCallback((event, picker) => {
        try {
            const changedStartDate = format(new Date(picker.startDate), "yyyy-MM-dd hh:mm:ss a");
            setDateTime(changedStartDate);
            setPickerDate(picker.startDate);
            setDateTimeError('');
        } catch (error) {
        }
    }, []);

    const handleSelectToggle = useCallback((value) => {
        setSelectValue(prev => {
            if (prev.includes(value)) {
                return prev.filter(item => item !== value);
            } else {
                return [...prev, value];
            }
        });
        setMhError('');
    }, []);

    const handleNotContentChange = (e) => {
        setNotContent(e.target.value);
        setNotifyError('');
    };

    const handleNotContentFocus = () => {
        if (notContent === 'Notification Content here') {
            setNotContent('');
        }
    };

    const handleNotContentBlur = () => {
        if (notContent === '') {
            setNotContent('Notification Content here');
        }
    };

    const createNotification = useCallback(async (Not_Heading, Not_Content, UserFor, PublichedUserEmail, PublichedUserType, PublishedOn) => {
        try {
            const apidata = await userService.SaveNotification(Not_Heading, Not_Content, UserFor, PublichedUserEmail, PublichedUserType, PublishedOn);
            
            if (apidata.length > 0) {
                if (apidata[0].statuscode == "1") {
                    setSuccessMessage(apidata[0].statusmessage);
                    setError('');
                    setLoading(false);
                    setTimeout(() => {
                        setSuccessMessage('');
                        setNotContent('Notification Content here');
                        setSelectValue([]);
                        setDateTime('Select Date & Time');
                    }, 4000);
                    
                    try {
                        const connection = new signalR.HubConnectionBuilder()
                            .withUrl("/notification", {
                                skipNegotiation: true,
                                transport: signalR.HttpTransportType.WebSockets
                            })
                            .build();
                        
                        await connection.start();
                        await connection.invoke("UpdateNotify", PublichedUserEmail, UserFor);
                    } catch (err) {
                    }
                } else {
                    setError(apidata[0].statusmessage);
                    setSuccessMessage('');
                    setLoading(false);
                    setTimeout(() => setError(''), 4000);
                }
            } else {
                setError('Error, Please try again.');
                setSuccessMessage('');
                setLoading(false);
            }
        } catch (error) {
            setError('Error, Please try again.');
            setSuccessMessage('');
            setLoading(false);
        }
    }, []);

    const schedule = useCallback(() => {
        try {
            const notHeading = 'N/A';
            const notContentValue = notContent;
            const checkAll = selectValue.filter(user => user === 'All');
            const usedFor = checkAll[0] === 'All' ? 'All' : (selectValue.length > 0 ? selectValue.join(',') : 'Broker');
            const email = userdata.email;
            const userType = userdata.userrole;
            const startDate = new Date();
            const endDate = pickerDate;
            let timeSpan = 0;
            
            if (endDate !== '' && endDate !== undefined && endDate !== null) {
                // Fixed: Calculate time difference correctly (future - now = positive)
                timeSpan = Math.round((endDate.getTime() - startDate.getTime()) / 1000);
            }
            
            const publishedOn = dateTime;
            const publish_utc_date = (new Date(Date.parse(publishedOn))).toUTCString();
            let count = 0;

            // Validate message
            if (!notContentValue || notContentValue === "Notification Content here") {
                setNotifyError('Notification required');
                count++;
            } else if (notContentValue.length > 100) {
                setNotifyError('Notification content exceeded 100 character limits');
                count++;
            }

            // Validate date and time
            if (!publishedOn || publishedOn === 'Select Date & Time') {
                setDateTimeError('Date and time required');
                count++;
            } else if (timeSpan < 0) {
                // Fixed: Check if scheduled time is in the past (negative timeSpan)
                setDateTimeError('Schedule date & time must be in the future');
                setDateTime('Select Date & Time');
                count++;
            }

            if (count === 0) {
                setLoading(true);
                createNotification(notHeading, notContentValue, usedFor, email, userType, publish_utc_date);
            }
        } catch (error) {
            setError('Error');
            setLoading(false);
        }
    }, [notContent, selectValue, dateTime, pickerDate, userdata, createNotification]);

    const closeNotification = () => {
        closeCreateNotification(cPage);
        OpenNotification(cPage);
    };

    return (
        <Dialog open={true} onOpenChange={closeNotification}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{text || 'Create New Notification'}</DialogTitle>
                </DialogHeader>

                {isMobile ? (
                    <MobileRestrictionMessage />
                ) : (
                <>
                <div className="space-y-4">
                    <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
                            {/* Multi-select for User Types */}
                            <div className="space-y-2">
                                <Label>User Type</Label>
                                <Popover open={expanded} onOpenChange={setExpanded}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="flex h-9 w-full items-center justify-between whitespace-nowrap"
                                        >
                                            <div className="flex flex-wrap gap-1">
                                                {selectValue.length === 0 ? (
                                                    <span className="text-muted-foreground">Select user types</span>
                                                ) : (
                                                    selectValue.map((val, idx) => (
                                                        <span key={idx} className="px-2 py-0.5 bg-primary/10 text-primary rounded text-sm">
                                                            {val}
                                                        </span>
                                                    ))
                                                )}
                                            </div>
                                            <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
                                        <div className="p-2">
                                            {options.map((option) => (
                                                <div key={option.value} className="flex items-center space-x-2 px-2 py-1 hover:bg-muted rounded cursor-pointer mb-0">
                                                    <Checkbox
                                                        id={option.value}
                                                        checked={selectValue.includes(option.value)}
                                                        onCheckedChange={() => handleSelectToggle(option.value)}
                                                    />
                                                    <label
                                                        htmlFor={option.value}
                                                        className="flex-1 cursor-pointer text-sm"
                                                    >
                                                        {option.value}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </PopoverContent>
                                </Popover>
                                {mhError && (
                                    <p className="text-sm text-destructive">{mhError}</p>
                                )}
                            </div>

                            {/* Notification Content */}
                            <div className="space-y-2">
                                <Label>Notification Content</Label>
                                <Textarea
                                    value={notContent}
                                    onChange={handleNotContentChange}
                                    onFocus={handleNotContentFocus}
                                    onBlur={handleNotContentBlur}
                                    rows={4}
                                    maxLength={100}
                                    className="bg-card"
                                />
                                {notifyError && (
                                    <p className="text-sm text-destructive">{notifyError}</p>
                                )}
                            </div>

                            {/* Date & Time Picker */}
                            <div className="space-y-2">
                                <Label>Schedule Date & Time</Label>
                                <DateRangePicker 
                                    className="w-full"
                                    compact={true}
                                    popoverClassName="[&_[data-slot=calendar]]:!min-w-0 [&_[data-slot=calendar]]:!max-w-full [&_[data-slot=calendar]]:w-auto"
                                    calendarClassName="p-1 [--cell-size:2.1rem]"
                                    calendarClassNames={{
                                        months: "flex flex-col gap-1 md:flex-row",
                                        month: "flex w-full flex-col gap-1",
                                        nav: "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1 h-7 px-1",
                                        dropdowns: "flex h-7 w-full items-center justify-center gap-1.5 text-xs font-semibold",
                                        dropdown_root: "relative rounded-md border bg-card px-1.5 py-0.5 min-w-[70px] cursor-pointer hover:bg-accent transition-colors h-6 flex items-center text-[11px]",
                                        caption_label: "select-none font-medium [&>svg]:text-muted-foreground flex h-6 items-center gap-1 rounded-md pl-1.5 pr-0.5 text-[11px] [&>svg]:size-3",
                                        month_caption: "flex h-7 w-full items-center justify-center px-7",
                                        weekdays: "flex",
                                        weekday: "text-muted-foreground w-[2.1rem] select-none text-xs font-medium py-1 flex items-center justify-center",
                                        day: "w-[2.1rem] h-[2.1rem] aspect-square p-0.5 text-center select-none group/day relative font-medium text-xs",
                                        button_previous: "h-4 w-4 select-none p-0 aria-disabled:hidden hover:bg-accent [&>svg]:size-4 mt-2 rounded-none",
                                        button_next: "h-4 w-4 select-none p-0 aria-disabled:opacity-50 hover:bg-accent [&>svg]:size-4 mt-2 rounded-none ms-auto",
                                    }}
                                    initialSettings={{
                                        singleDatePicker: true,
                                        timePicker: true,
                                        timePicker24Hour: false,
                                        timePickerIncrement: 1,
                                        minDate: new Date(),
                                        showDropdowns: false,
                                        autoUpdateInput: false,
                                        locale: {
                                            format: 'YYYY-MM-DD hh:mm:ss A'
                                        }
                                    }}
                                    onApply={handleCalendar}
                                >
                                    <div className="flex items-center gap-2 w-full h-10 rounded-md border border-input bg-card px-3 cursor-pointer hover:shadow-sm transition-shadow">
                                        <img className="h-5 w-5 flex-shrink-0 text-muted-foreground" src={Calendar} alt="Date and time" />
                                        <Input
                                            type="text"
                                            className="bg-transparent text-sm cursor-pointer flex-1 border-0 outline-none h-auto p-0 rounded-none placeholder:text-muted-foreground"
                                            value={dateTime}
                                            readOnly
                                        />
                                    </div>
                                </DateRangePicker>
                                {dateTimeError && (
                                    <p className="text-sm text-destructive">{dateTimeError}</p>
                                )}
                            </div>

                            {error && (
                                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                                    {error}
                                </div>
                            )}

                            {successMessage && (
                                <div className="text-sm text-green-700 bg-green-50 p-3 rounded-md border border-green-200">
                                    {successMessage}
                                </div>
                            )}
                        </form>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            onClick={schedule}
                            disabled={loading}
                        >
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Schedule Notification
                        </Button>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={closeNotification}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                    </DialogFooter>
                </>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default CreateNotification;
